import { useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronRight, FaLock, FaSearch } from "react-icons/fa";
import { hasColumn, sourceEntity } from "../definition";
import { labelOf } from "../labels";
import type { Catalog, CatalogEntity, CatalogRelation, ReportSpec } from "../types";
import { input } from "./styles";

type Item = { path: string; label: string; sensitive?: boolean };
type Group = { key: string; title: string; many: boolean; ownOnly: boolean; items: Item[] };

type Props = {
  catalog: Catalog;
  definition: ReportSpec;
  onToggle: (path: string) => void;
};

/** The objects and fields a report can show, grouped by how they relate to the subject. */
export default function FieldPanel({ catalog, definition, onToggle }: Props): ReactElement {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [closed, setClosed] = useState<Record<string, boolean>>({});

  const groups = useMemo<Group[]>(() => {
    const root = sourceEntity(catalog, definition.source);
    if (!root) return [];
    const fieldsOf = (entity: CatalogEntity, prefix: string): Item[] =>
      entity.fields.map((field) => ({ path: prefix + field.key, label: labelOf(t, field), sensitive: field.sensitive }));
    const result: Group[] = [{
      key: "",
      title: labelOf(t, root),
      many: false,
      ownOnly: root.restrictedToOwnClasses,
      items: fieldsOf(root, ""),
    }];
    const addRelation = (relation: CatalogRelation, prefix: string, parentTitle?: string, depth = 0) => {
      const target = catalog.entities[relation.entity];
      if (!target) return;
      const many = relation.cardinality === "MANY";
      const items = fieldsOf(target, `${prefix}${relation.key}.`);
      if (many) {
        items.unshift({
          path: `${prefix}${relation.key}`,
          label: String(t("reports.builder.count", { defaultValue: "Number" })),
        });
      }
      const title = parentTitle ? `${parentTitle} › ${labelOf(t, relation)}` : labelOf(t, relation);
      result.push({ key: `${prefix}${relation.key}`, title, many, ownOnly: target.restrictedToOwnClasses, items });
      // Relations with one value further on (a child's class → its school year); at most three steps.
      if (depth < 1) {
        target.relations
          .filter((nested) => nested.cardinality === "ONE")
          .forEach((nested) => addRelation(nested, `${prefix}${relation.key}.`, title, depth + 1));
      }
    };
    root.relations.forEach((relation) => addRelation(relation, ""));
    return result;
  }, [catalog, definition.source, t]);

  const needle = query.trim().toLowerCase();
  const visible = groups
    .map((group) => ({
      ...group,
      items: needle
        ? group.items.filter((item) => `${group.title} ${item.label}`.toLowerCase().includes(needle))
        : group.items,
    }))
    .filter((group) => group.items.length > 0);

  return (
    <nav aria-label={String(t("reports.builder.fields_title", { defaultValue: "Data" }))} className="space-y-3">
      <label className="relative block">
        <span className="sr-only">{t("reports.builder.search_fields", { defaultValue: "Search data" })}</span>
        <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400" />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={String(t("reports.builder.search_fields", { defaultValue: "Search data" }))}
          className={`${input} pl-8`}
        />
      </label>

      {visible.length === 0 && (
        <p className="px-1 text-sm text-slate-500 dark:text-slate-400">
          {t("reports.builder.no_fields_found", { defaultValue: "Nothing found." })}
        </p>
      )}

      {visible.map((group) => {
        const open = needle !== "" || !closed[group.key];
        return (
          <section key={group.key || "root"} className="rounded-2xl border border-slate-200 dark:border-white/10">
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setClosed((current) => ({ ...current, [group.key]: open }))}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-bold text-slate-800 dark:text-slate-100"
            >
              {open ? <FaChevronDown className="text-xs" /> : <FaChevronRight className="text-xs" />}
              <span className="min-w-0 flex-1 truncate">{group.title}</span>
              {group.many && (
                <span
                  className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-white/10 dark:text-slate-300"
                  title={String(t("reports.builder.many_hint", {
                    defaultValue: "Several per row: combined in one cell",
                  }))}
                >
                  {t("reports.builder.many", { defaultValue: "several" })}
                </span>
              )}
            </button>
            {open && (
              <div className="space-y-0.5 px-2 pb-2">
                {group.ownOnly && (
                  <p className="px-2 pb-1 text-xs text-slate-500 dark:text-slate-400">
                    {t("reports.builder.own_classes_only", { defaultValue: "Only from your own classes" })}
                  </p>
                )}
                {group.items.map((item) => (
                  <label
                    key={item.path}
                    className="flex cursor-pointer items-center gap-3 rounded-xl px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-white/5"
                  >
                    <input
                      type="checkbox"
                      checked={hasColumn(definition, item.path)}
                      onChange={() => onToggle(item.path)}
                      className="h-4 w-4 accent-indigo-600"
                    />
                    {item.label}
                    {item.sensitive && (
                      <FaLock
                        className="ml-auto text-xs text-amber-600 dark:text-amber-300"
                        title={String(t("reports.builder.sensitive_hint", {
                          defaultValue: "Sensitive data: viewing and exporting is logged",
                        }))}
                        aria-label={String(t("reports.builder.sensitive", { defaultValue: "Sensitive" }))}
                      />
                    )}
                  </label>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </nav>
  );
}
